import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";

import debounce from "lodash.debounce";

import { useClickOutside } from "../../Hooks/useClickOutside";
import SidebarTransition from "../MacroSidebar/SidebarTransition";
import Portal from "../Portal";

import LinkTab from "./Components/Link";
import Attachments from "./Components/Attachments";
import Unsplash from "./Components/Unsplash";
import Library from "./Components/Library";
import Video from "./Components/Video";
import Users from "./Components/Users";

import { Content, SidebarContainer } from "./styled/StyledComponents";

import { TAB_SOURCES } from "./TabSources";
import { BackgroundDialogContext } from "./Context";

function BackgroundPopup({
  image,
  setImage,
  isOpen,
  onClose,
  dialogPositionCss,
  position = "fixed",
  colors = [],
  setIsDraggingEnabled,
  showVideo = false,
  showCurrentPageInAttachments = true,
  showVideoInAttachments = true,
  showUsers = false,
  isMulti = false,
  linkToDocs = "",
  imageOrientation = {},
  imageColor = {},
}) {
  const [selectedTab, setSelectedTab] = useState(TAB_SOURCES.Unsplash);
  const [zoom, setZoom] = useState(1);
  const [contentType, setContentType] = useState("page");

  const imageRef = useRef();
  imageRef.current = image;

  const popupRef = useRef(null);
  useClickOutside(popupRef, () => onClose());

  const availableTabs = Object.values(TAB_SOURCES).filter(
    (tab) => (showUsers || tab !== "Users") && (showVideo || tab !== "Video")
  );

  useEffect(() => {
    window.AP.context.getContext((ctx) =>
      setContentType(ctx?.confluence?.content?.type || "blogpost")
    );
  }, []);

  const getProps = {
    image,
    colors,
    isMulti,
    linkToDocs,
    contentType,
  };

  const setProps = { setSelectedTab, setImage };

  useEffect(() => {
    if (image?.zoom) {
      setZoom(image.zoom);
    }
  }, [image]);

  useEffect(() => {
    setIsDraggingEnabled(!isOpen);
  }, [isOpen]);

  const handleZoomChangesDebounce = useRef(
    debounce((newZoom) => {
      const curZoom = imageRef?.current?.zoom;

      if (curZoom !== undefined && curZoom !== newZoom) {
        setImage({ ...imageRef.current, zoom: newZoom });
      }
    }, 300)
  ).current;

  useEffect(() => {
    return () => {
      handleZoomChangesDebounce.cancel();
    };
  }, [handleZoomChangesDebounce]);

  useEffect(() => {
    handleZoomChangesDebounce(zoom);
  }, [zoom]);

  return (
    <Portal contentVisible={isOpen}>
      <BackgroundDialogContext.Provider value={{ ...getProps, ...setProps }}>
        <SidebarContainer
          dialogPositionCss={dialogPositionCss}
          position={position}
          ref={popupRef}
        >
          <SidebarTransition
            key="background-popup"
            isOpen={isOpen}
            position="right"
            close={() => onClose(image.source)}
            width={420}
            showTabs
            shouldHideScrollbar
            className="selector-sidebar"
            selectedTab={selectedTab}
            setSelectedTab={setSelectedTab}
            tabs={availableTabs}
            containerStyles={{ zIndex: "10005" }}
          >
            <Content>
              {selectedTab === TAB_SOURCES.Library && <Library />}
              {selectedTab === TAB_SOURCES.Video && <Video />}
              {selectedTab === TAB_SOURCES.Users && <Users />}
              {selectedTab === TAB_SOURCES.Unsplash && isOpen && (
                <Unsplash
                  imageOrientation={imageOrientation}
                  imageColor={imageColor}
                />
              )}
              {selectedTab === TAB_SOURCES.Link && <LinkTab />}
              {selectedTab === TAB_SOURCES.Attachments && (
                <Attachments
                  showCurrentPageInAttachments={showCurrentPageInAttachments}
                  showVideoInAttachments={showVideoInAttachments}
                />
              )}
            </Content>
          </SidebarTransition>
        </SidebarContainer>
      </BackgroundDialogContext.Provider>
    </Portal>
  );
}

BackgroundPopup.propTypes = {
  image: PropTypes.object.isRequired,
  setImage: PropTypes.func.isRequired,
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  setIsDraggingEnabled: PropTypes.func,
  dialogPositionCss: PropTypes.shape({
    top: PropTypes.string,
    bottom: PropTypes.string,
    right: PropTypes.string,
    left: PropTypes.string,
  }),
  position: PropTypes.string,
  colors: PropTypes.array,
  showVideo: PropTypes.bool,
  showUsers: PropTypes.bool,
  linkToDocs: PropTypes.string,
  isMulti: PropTypes.bool,
};

BackgroundPopup.defaultProps = {
  colors: [],
  linkToDocs: "",
  position: "fixed",
  showVideo: false,
  showUsers: false,
  isMulti: false,
  setIsDraggingEnabled() {},
  dialogPositionCss: {
    bottom: "0",
  },
};

export default BackgroundPopup;
